import { Currencies, TransferSources } from '@generated/graphql';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import { AccountTypes, CurrencyCodes } from '@onevesthq/ov-enums';
import dayjs, { type Dayjs } from 'dayjs';

/**
 * Blocks submit when withdrawing from RESP/FHSA unless the transfer is an
 * adjustment and the user has `write:registered_adjustment_transfer`.
 * Shared with `newTransfer.tsx` and adjustment transfer creation UI.
 */
export const isBlockedByRestrictedAccountWithdraw = ({
  isRespWithdraw,
  isFHSAWithdraw,
  transferSource,
  canAdjustRestrictedAccounts,
}: {
  isRespWithdraw: boolean;
  isFHSAWithdraw: boolean;
  transferSource: string;
  canAdjustRestrictedAccounts: boolean;
}): boolean => {
  if (!isRespWithdraw && !isFHSAWithdraw) return false;
  if (
    transferSource === TransferSources.Adjustment &&
    canAdjustRestrictedAccounts
  )
    return false;
  return true;
};

export const RESP_ACCOUNT_TYPES: AccountTypes[] = [
  AccountTypes.RESP_SINGLE,
  AccountTypes.RESP_FAMILY,
  AccountTypes.RESP_ADULT,
  AccountTypes.RESP_SINGLE_JOINT,
  AccountTypes.RESP_FAMILY_JOINT,
];

export const isRespAccountType = (accountType: string): boolean =>
  RESP_ACCOUNT_TYPES.includes(accountType as AccountTypes);

export const isFhsaAccountType = (accountType: string): boolean =>
  accountType === AccountTypes.FHSA;

export const currencyCodesToCurrencies = (
  code: CurrencyCodes | undefined,
): Currencies | undefined => {
  if (code === CurrencyCodes.CAD) return Currencies.Cad;
  if (code === CurrencyCodes.USD) return Currencies.Usd;
  return undefined;
};

export const getTransferCurrencyOptions = (
  subAccount: SubAccount | undefined,
): Currencies[] => {
  const acc = subAccount?.account;
  if (!acc) return [];
  const fromAvailable = new Set<Currencies>();
  for (const c of acc.availableCurrencies ?? []) {
    const mapped = currencyCodesToCurrencies(c);
    if (mapped) fromAvailable.add(mapped);
  }
  if (fromAvailable.size > 0) return Array.from(fromAvailable);
  const fromBase = currencyCodesToCurrencies(acc.baseCurrency);
  return fromBase ? [fromBase] : [];
};

// holding-service resolves sourceId to a SubAccount for all four of these
// (transferResolvers.ts:202-210), including the *_ACCOUNT sources.
export const SUB_ACCOUNT_SOURCES: string[] = [
  TransferSources.ToSubAccount,
  TransferSources.FromSubAccount,
  TransferSources.ToAccount,
  TransferSources.FromAccount,
];

export const SOURCE_ID_REQUIRED_SOURCES: string[] = [
  TransferSources.Fee,
  ...SUB_ACCOUNT_SOURCES,
];

export type CreatedDateRange = {
  createdFrom?: Dayjs | null;
  createdTo?: Dayjs | null;
};

// The API filters on key presence, so an empty bound must be omitted, never sent as null
export const buildCreatedDateFilter = ({
  createdFrom,
  createdTo,
}: CreatedDateRange) => ({
  ...(createdFrom && dayjs(createdFrom).isValid()
    ? { createdAtAfter: dayjs(createdFrom).startOf('day').toISOString() }
    : {}),
  ...(createdTo && dayjs(createdTo).isValid()
    ? { createdAtBefore: dayjs(createdTo).endOf('day').toISOString() }
    : {}),
});

export const hasCreatedDateFilter = (range: CreatedDateRange) =>
  Object.keys(buildCreatedDateFilter(range)).length > 0;
